import { Helmet } from 'react-helmet-async';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Stack, IconButton, Menu, MenuItem } from '@mui/material';
import { InvoiceView, QueueNextButton } from '../../../components/dashboard/invoicing';
import { useDispatch } from '../../../redux/store';
import { getInvoiceBreakdown } from '../../../redux/slices/invoicing';
import Iconify from '../../../reusable-components/iconify';

export default function Invoice() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] = useState(null);
  const section = location.pathname.split('/')[3];
  const [hovered, setHovered] = useState(false);
  const invoicesInQueue = JSON.parse(localStorage.getItem('selectedInvoices') || '[]');
  const invoicesLeft = invoicesInQueue?.length || 0;
  const initialMenuItems = [
    { id: 'info', label: 'Invoice Information' },
    { id: 'pdf', label: 'PDF Viewer' },
    { id: 'log', label: 'Invoice Log' },
  ];
  const [menuItems, setMenuItems] = useState(initialMenuItems);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragOver = (index) => {
    if (draggedIndex === null || draggedIndex === index) return;
    const updated = [...menuItems];
    const [dragged] = updated.splice(draggedIndex, 1);
    updated.splice(index, 0, dragged);
    setMenuItems(updated);
    setDraggedIndex(index);
  };
  const handleDragEnd = () => setDraggedIndex(null);

  useEffect(() => {
    async function fetchData() {
      await dispatch(getInvoiceBreakdown(id));
    }
    fetchData();
  }, [id]);

  return (
    <>
      <Helmet>
        <title>Invoicing | Platform Solutions</title>
      </Helmet>
      <Stack direction={'column'}>
        <Box sx={{ justifyContent: 'space-between', mb: '5px', display: 'flex' }}>
          <Button
            variant="text"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => {
              if (section === 'previous')
                navigate(
                  `/dashboard/invoicing/previous?start=${new Date(
                    new Date().setDate(new Date().getDate() - 29)
                  ).toISOString()}&end=${new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString()}`
                );
              else navigate(`/dashboard/invoicing/${section}`);
            }}
            startIcon={<Iconify icon="mingcute:left-fill" width={24} height={24} sx={{ mx: -1 }} />}
            sx={{
              color: '#454F5B',
              fontWeight: 'bold',
              justifyContent: 'start',
            }}
          >
            {hovered && `Back to invoices`}
          </Button>
          <Box sx={{display: 'flex'}}>
            {invoicesLeft > 1 && <QueueNextButton />}
            <IconButton onClick={(e) => setSettingsMenuAnchorEl(e.currentTarget)}>
              <Iconify icon="mdi:settings" width={24} height={24} />
            </IconButton>
            <Menu
              anchorEl={settingsMenuAnchorEl}
              open={Boolean(settingsMenuAnchorEl)}
              onClose={() => setSettingsMenuAnchorEl(null)}
              sx={{ minWidth: 220 }}
            >
              {menuItems.map((item, idx) => (
                <MenuItem
                  key={item.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => { e.preventDefault(); handleDragOver(idx); }}
                  onDragEnd={handleDragEnd}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: draggedIndex === idx ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    opacity: draggedIndex === idx ? 0.5 : 1,
                    borderTop: (theme) => draggedIndex === idx ? `1px solid ${theme.palette.secondary.main}` : 'none'
                  }}
                >
                  <DragIndicatorIcon sx={{ mr: 1 }} />
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
        <InvoiceView invoiceColumnsOrder={menuItems.map(item => item.id)} />
      </Stack>
    </>
  );
}
